import { Button, Dialog, Group, Spinner, Table3 } from '@economic/taco';
import { Trans, t } from '@lingui/macro';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormApi } from 'final-form';

import { Form } from '~/app/shared/components/Form';
import { useQuery } from '~/app/shared/hooks/useQuery';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import number from '~/app/shared/util/utilities/number';
import { PartialInvoiceDistributionType, PartialInvoicingViewLineDto } from '~/types/internal-api';
import track from '~/mixpanel-service';
import { validationSchema } from './CreateProductLineBasedPartialInvoice.validator';
import ErrorMessageAlert from './ErrorMessageAlert';
import QuantityForInvoicingInputHandler from './QuantityForInvoicingInputHandler';

interface Props {
    salesDocumentId: number;
    close: () => void;
    handleShowConvertToInvoiceConfirmation: () => void;
    hasDraftPartialInvoices: boolean;
    partialInvoicesCount: number;
}

export default function LineBasedCreatePartialInvoiceForm({
    salesDocumentId,
    close,
    handleShowConvertToInvoiceConfirmation,
    hasDraftPartialInvoices,
    partialInvoicesCount,
}: Props) {
    const navigate = useNavigate();

    const { data, loading } = useQuery<PartialInvoicingViewLineDto[]>(
        `sales/partial-invoice/partial-invoicing-view-model/${salesDocumentId}`
    );

    const sortedQueryData = useMemo(() => (loading ? [] : data.sort((a, b) => a.sortKey - b.sortKey)), [data, loading]);

    if (loading) {
        return <Spinner />;
    }

    const disableSubmitButton = values => {
        // if we are not invoicing anything (no input with value more than 0) we should disable the submit button
        let result = true;
        Object.keys(values).forEach(key => {
            const fieldValue = values[key];
            if (key.startsWith('newQuantity-') && fieldValue > 0) {
                result = false;
            }
        });
        return result;
    };

    const shouldShowConvertToInvoiceButton = values => {
        let isInvoicingAllRemainingLines = true;
        data.forEach(orderLine => {
            const remainingQuantity = orderLine.quantity - orderLine.alreadyInvoiced;
            const quantityToInvoice = values[`newQuantity-${orderLine.lineId}`];
            if (remainingQuantity !== 0 && remainingQuantity !== quantityToInvoice) {
                isInvoicingAllRemainingLines = false;
            }
        });

        return isInvoicingAllRemainingLines;
    };

    const submitLineBased = async values => {
        const requestLines: { lineId: number; quantity: number }[] = [];
        Object.keys(values).forEach(key => {
            const fieldValue = values[key];
            if (fieldValue > 0) {
                const lineId = key.substring(key.indexOf('-') + 1);
                requestLines.push({ lineId: parseInt(lineId), quantity: fieldValue });
            }
        });
        const request = {
            orderId: salesDocumentId,
            lines: requestLines,
        };

        await internalApiClient
            .post(`/sales/partial-invoice/product-line-based`, request)
            .then(response => {
                const partialInvoiceSalesDocumentId = response.data;
                track('partial-invoicing', 'created', {
                    distributionType: PartialInvoiceDistributionType.ProductLineBased,
                    invoiceCount: partialInvoicesCount + 1,
                });
                navigate(`sales/invoicing/invoices/${partialInvoiceSalesDocumentId}`);
            })
            .catch(error => {
                return Promise.reject(error);
            });
    };

    const handleBalanceAll = (form: FormApi) => {
        form.batch(() => {
            data.filter(orderLine => orderLine.quantity - orderLine.alreadyInvoiced > 0).forEach(orderLine => {
                form.change(`newQuantity-${orderLine.lineId}`, orderLine.quantity - orderLine.alreadyInvoiced);
            });
        });
    };

    return (
        <div>
            <Form onSubmit={submitLineBased} validationSchema={validationSchema(data)} messages={{ success: t`Success` }}>
                {({ form, values, errors }) => (
                    <>
                        <div className="ml-56 mb-4 -mt-12">
                            <Button onClick={() => handleBalanceAll(form)} className="ml-2">
                                <Trans>Balance all</Trans>
                            </Button>
                        </div>
                        <div className="max-h-[250px] flex-grow overflow-y-auto flex flex-shrink flex-col">
                            <Table3<PartialInvoicingViewLineDto>
                                data={sortedQueryData}
                                id="create-partial-invoice-table"
                                enableColumnResizing>
                                <Table3.Column<PartialInvoicingViewLineDto> id="sortKey" header={t`Line No.`} />
                                <Table3.Column<PartialInvoicingViewLineDto> id="productId" header={t`Product no.`} />
                                <Table3.Column<PartialInvoicingViewLineDto>
                                    id="quantity"
                                    accessor="quantity"
                                    header={t`Quantity`}
                                    renderer={value => number.format(value)}
                                />
                                <Table3.Column<PartialInvoicingViewLineDto>
                                    id="alreadyInvoiced"
                                    accessor="alreadyInvoiced"
                                    header={t`Previously invoiced`}
                                    renderer={value => number.format(value)}
                                />
                                <Table3.Column<PartialInvoicingViewLineDto>
                                    id="newQuantity"
                                    accessor="newQuantity"
                                    header={t`Quantity for invoicing`}
                                    renderer={(_, row) => (
                                        <QuantityForInvoicingInputHandler
                                            quantity={row.quantity}
                                            alreadyInvoiced={row.alreadyInvoiced}
                                            form={form}
                                            fieldId={`newQuantity-${row.lineId}`}
                                            fieldValue={values[`newQuantity-${row.lineId}`]}
                                        />
                                    )}
                                />
                            </Table3>
                        </div>
                        <ErrorMessageAlert
                            errors={errors}
                            shouldShowConvertToInvoiceButton={shouldShowConvertToInvoiceButton(values)}
                            hasDraftPartialInvoices={hasDraftPartialInvoices}
                        />
                        <Dialog.Footer>
                            <Group>
                                <Button onClick={close}>
                                    <Trans>Cancel</Trans>
                                </Button>
                                {shouldShowConvertToInvoiceButton(values) ? (
                                    <Button
                                        appearance="primary"
                                        onClick={() => handleShowConvertToInvoiceConfirmation()}
                                        disabled={hasDraftPartialInvoices}>
                                        <Trans>Create partial invoice</Trans>
                                    </Button>
                                ) : (
                                    <Button appearance="primary" type="submit" disabled={disableSubmitButton(values)}>
                                        <Trans>Create partial invoice</Trans>
                                    </Button>
                                )}
                            </Group>
                        </Dialog.Footer>
                    </>
                )}
            </Form>
        </div>
    );
}
